import { getBffRequestHeaders } from '@/lib/api/bff-request-headers';
import { notifyBffUnauthorized } from '@/lib/auth/auth-watchdog';
import { preloadChannelImages } from '@/lib/chat/preload-chat-images';
import { throwApiError } from '@/utils/error-to-user-message';

const CHAT_API_PREFIX = '/api/bff/chat';

// ── Persistent storage helpers (тёплый старт между перезагрузками) ─────────────
// Кэши чата живут не только в памяти модуля (которая умирает при F5 / открытии
// новой вкладки), но и в Web Storage — чтобы при холодном открытии страницы лента
// и список каналов рисовались мгновенно из локального снимка, а сеть лишь
// досинхронизировала их в фоне (stale-while-revalidate).
//
//  • localStorage — стабильные данные (список каналов, bundle канала, резолв
//    проекта→спейс). Переживает рестарт браузера и новую вкладку.
//  • sessionStorage — «голова» ленты сообщений (волатильна, чистится при закрытии
//    вкладки → меньше риск показать сильно устаревшую переписку чужому глазу).
const IS_SSR = typeof window === 'undefined';

function localStore(): Storage | null {
  if (IS_SSR) return null;
  try {
    return window.localStorage;
  } catch {
    return null; // приватный режим / отключённое хранилище
  }
}
function sessionStore(): Storage | null {
  if (IS_SSR) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
function readJSON<T>(storage: Storage | null, key: string): T | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeJSON(storage: Storage | null, key: string, value: unknown): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceeded / приватный режим — кэш не критичен, молча игнорируем.
    // Под квоту чаще всего попадает sessionStorage сообщений: подчистим LRU.
  }
}
function clearByPrefix(storage: Storage | null, prefix: string): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => storage.removeItem(k));
  } catch {
    /* ignore */
  }
}

// ── Channel list response cache ───────────────────────────────────────────────
// Кэш ответов listChannels: prefetch из ChatSidebarBody заполняет кэш,
// ChannelList при монтировании читает уже готовые данные без задержки.
// FRESH — окно, в котором chatApi.listChannels отдаёт кэш без обращения к сети;
// STALE_MAX — за ним снимок считается слишком старым и не показывается даже как
// «мгновенный» (чтобы не светить совсем протухший список). Между ними —
// stale-while-revalidate: показываем сразу, обновляем в фоне.
const LIST_CHANNELS_FRESH_MS = 15_000; // 15 секунд
const LIST_CHANNELS_STALE_MAX_MS = 5 * 60_000; // 5 минут
const LIST_CHANNELS_LS_PREFIX = 'chat_cache_channels_';
type ListCacheEntry = { data: any; ts: number };
const listChannelsCache = new Map<string, ListCacheEntry>();
// Дедупликация параллельных запросов с одинаковыми параметрами
const listChannelsInflight = new Map<string, Promise<any>>();

function lsKeyChannels(qs: string): string {
  return LIST_CHANNELS_LS_PREFIX + qs;
}

/** Читает запись из памяти, при промахе — гидрирует из localStorage. null если старше STALE_MAX. */
function readListChannelsEntry(qs: string): ListCacheEntry | null {
  let entry = listChannelsCache.get(qs);
  if (!entry) {
    const persisted = readJSON<ListCacheEntry>(localStore(), lsKeyChannels(qs));
    if (persisted && persisted.data) {
      entry = persisted;
      listChannelsCache.set(qs, persisted);
    }
  }
  if (!entry) return null;
  if (Date.now() - entry.ts > LIST_CHANNELS_STALE_MAX_MS) return null;
  return entry;
}

function storeListChannelsEntry(qs: string, data: any): void {
  const entry: ListCacheEntry = { data, ts: Date.now() };
  listChannelsCache.set(qs, entry);
  writeJSON(localStore(), lsKeyChannels(qs), entry);
}

/**
 * Синхронное чтение кэша listChannels — для инициализации state без ожидания.
 * Отдаёт даже устаревший (в пределах STALE_MAX) снимок: вызывающая сторона всё
 * равно запускает фоновую ревалидацию, а пользователь видит список сразу.
 */
export function getListChannelsCache(params?: Record<string, string>): any[] | null {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const entry = readListChannelsEntry(qs);
  return (entry?.data?.items as any[]) ?? null;
}

/**
 * Сброс кэша listChannels (после создания/удаления канала и т.п.).
 * Без аргументов — очистить весь кэш; иначе — только запись с теми же query-параметрами.
 */
export function invalidateListChannelsCache(params?: Record<string, string>) {
  if (!params || Object.keys(params).length === 0) {
    listChannelsCache.clear();
    clearByPrefix(localStore(), LIST_CHANNELS_LS_PREFIX);
    return;
  }
  const qs = '?' + new URLSearchParams(params).toString();
  listChannelsCache.delete(qs);
  try {
    localStore()?.removeItem(lsKeyChannels(qs));
  } catch {
    /* ignore */
  }
}

// ── Channel enrichment cache ──────────────────────────────────────────────────
// Когда listChannels возвращает обогащённые данные каналов, сохраняем их —
// при переходе в MessageThread `getChannel` берёт из кэша вместо повторного запроса.
// Полные bundle'ы (с members) дополнительно персистятся в localStorage для
// тёплого старта шапки канала после перезагрузки.
const CHANNEL_FRESH_MS = 60_000; // окно, в котором getChannel отдаёт кэш без сети
const CHANNEL_STALE_MAX_MS = 10 * 60_000;
const CHANNEL_LS_PREFIX = 'chat_cache_channel_';
type ChannelCacheEntry = { data: any; ts: number };
const channelCache = new Map<string, ChannelCacheEntry>();

function lsKeyChannel(id: string): string {
  return CHANNEL_LS_PREFIX + id;
}

function cacheChannelFromList(channel: Record<string, unknown>) {
  const id = channel.id;
  if (typeof id !== 'string' || !id) return;
  // ВАЖНО: НЕ затираем уже закэшированный полный bundle (с members) частичной
  // записью из списка. Иначе ChannelInfoScreen после первого открытия начинает
  // показывать «пустой» список участников — getChannel вернёт listChannels-stub
  // вместо bundle с members. Прод-баг 2026-06-04.
  const existing = channelCache.get(id);
  if (existing && existing.data?.members !== undefined) return;
  channelCache.set(id, { data: { channel }, ts: Date.now() });
}

/** Сохранить ПОЛНЫЙ bundle канала (с members) в память и localStorage. */
function storeChannelBundle(id: string, data: any): void {
  const entry: ChannelCacheEntry = { data, ts: Date.now() };
  channelCache.set(id, entry);
  if (data && typeof data === 'object' && data.members !== undefined) {
    writeJSON(localStore(), lsKeyChannel(id), entry);
  }
}

function getCachedChannel(id: string): any | null {
  let entry = channelCache.get(id);
  if (!entry) {
    // Тёплый старт: гидрируем полный bundle из localStorage после перезагрузки.
    const persisted = readJSON<ChannelCacheEntry>(localStore(), lsKeyChannel(id));
    if (persisted && persisted.data?.members !== undefined) {
      entry = persisted;
      channelCache.set(id, persisted);
    }
  }
  if (!entry) return null;
  if (Date.now() - entry.ts > CHANNEL_FRESH_MS) {
    // За пределами FRESH не отдаём из getChannel — пусть сделает live-fetch, чтобы
    // список участников/настройки не висели устаревшими. (Тёплый старт шапки
    // обеспечивает отдельная синхронная отрисовка из route, см. MessageThread.)
    return null;
  }
  // Если в кэше — частичная запись из listChannels (без members), не отдаём её
  // вызывателям getChannel — пусть сделают live-fetch. Иначе ChannelInfoScreen
  // отрисует пустой список участников. Один лишний HTTP-запрос — приемлемо за
  // отсутствие визуальных гонок.
  if (entry.data?.members === undefined) return null;
  return entry.data;
}

/**
 * Stale-толерантное синхронное чтение ПОЛНОГО bundle канала (с members) — для
 * seed state без спиннера (ChannelView). В отличие от getChannel, отдаёт снимок
 * даже «остывшим» (в пределах STALE_MAX): UI всё равно ревалидирует в фоне.
 */
export function peekCachedChannel(id: string): any | null {
  let entry = channelCache.get(id);
  if (!entry) {
    const persisted = readJSON<ChannelCacheEntry>(localStore(), lsKeyChannel(id));
    if (persisted && persisted.data?.members !== undefined) {
      entry = persisted;
      channelCache.set(id, persisted);
    }
  }
  if (!entry) return null;
  if (Date.now() - entry.ts > CHANNEL_STALE_MAX_MS) return null;
  if (entry.data?.members === undefined) return null;
  return entry.data;
}

/** Сброс кэша одного канала (после изменения участников/настроек). */
export function invalidateChannelCache(channelId: string) {
  channelCache.delete(channelId);
  try {
    localStore()?.removeItem(lsKeyChannel(channelId));
  } catch {
    /* ignore */
  }
}

// ── Topics list cache ─────────────────────────────────────────────────────────
// Список топиков канала (для forum-режима) — SWR + персист, чтобы ChannelView
// рисовал список без спиннера при повторном/холодном открытии. Кэшируем только
// дефолтный вызов listTopics(channelId) без доп. параметров.
const TOPICS_FRESH_MS = 30_000;
const TOPICS_STALE_MAX_MS = 10 * 60_000;
const TOPICS_LS_PREFIX = 'chat_cache_topics_';
type TopicsCacheEntry = { data: any; ts: number };
const topicsCache = new Map<string, TopicsCacheEntry>();
const topicsInflight = new Map<string, Promise<any>>();

function lsKeyTopics(channelId: string): string {
  return TOPICS_LS_PREFIX + channelId;
}

function readTopicsEntry(channelId: string): TopicsCacheEntry | null {
  let entry = topicsCache.get(channelId);
  if (!entry) {
    const persisted = readJSON<TopicsCacheEntry>(localStore(), lsKeyTopics(channelId));
    if (persisted && persisted.data) {
      entry = persisted;
      topicsCache.set(channelId, persisted);
    }
  }
  if (!entry) return null;
  if (Date.now() - entry.ts > TOPICS_STALE_MAX_MS) return null;
  return entry;
}

function storeTopicsEntry(channelId: string, data: any): void {
  const entry: TopicsCacheEntry = { data, ts: Date.now() };
  topicsCache.set(channelId, entry);
  writeJSON(localStore(), lsKeyTopics(channelId), entry);
}

/** Синхронное чтение кэша топиков канала — для seed state в ChannelView без спиннера. */
export function getCachedTopics(channelId: string): any | null {
  return readTopicsEntry(channelId)?.data ?? null;
}

/** Сброс кэша топиков канала (после создания/удаления/reorder топика). */
export function invalidateTopicsCache(channelId?: string) {
  if (!channelId) {
    topicsCache.clear();
    clearByPrefix(localStore(), TOPICS_LS_PREFIX);
    return;
  }
  topicsCache.delete(channelId);
  try {
    localStore()?.removeItem(lsKeyTopics(channelId));
  } catch {
    /* ignore */
  }
}

// ── Messages SWR cache ────────────────────────────────────────────────────────
// Кэш последней «головы» ленты топика (первые ~50 сообщений) для stale-while-
// revalidate: при повторном входе в топик MessageThread синхронно читает кэш и
// рисует ленту МГНОВЕННО (без спиннера), параллельно дёргая listMessages для
// досинхронизации — как Telegram. Хранится УЖЕ нормализованный Message[] (после
// messageFromApiRow + filter в MessageThread) — чтобы на входе не гонять
// нормализацию повторно.
//
// SWR-on-expiry: за FRESH_MS снимок считаем «остывшим», но НЕ удаляем и всё равно
// отдаём (UI ревалидирует в фоне) — это убирает класс «прошло 30с → опять спиннер».
// Удаляем только за пределами STALE_MAX_MS.
//
// Тёплый старт между перезагрузками обеспечивает ОТДЕЛЬНЫЙ слой — «сырой» снимок
// головы в sessionStorage (getPersistedRawMessages): in-memory нормализованный
// кэш умирает при F5, а сырой снимок переживает и MessageThread нормализует его
// на маунте. Кэшируем ТОЛЬКО голову (limit=50, без before_seq).
const MESSAGES_FRESH_MS = 30_000; // 30 секунд — маркер «остывания», НЕ удаление
const MESSAGES_STALE_MAX_MS = 10 * 60_000; // 10 минут — жёсткий потолок
const RAW_MESSAGES_SS_PREFIX = 'chat_cache_msghead_';
const RAW_MESSAGES_MAX_TOPICS = 8; // LRU-кап в sessionStorage
const RAW_MESSAGES_INDEX_KEY = 'chat_cache_msghead_index';
type MessagesCacheEntry = { items: any[]; hasMore: boolean; ts: number };
const messagesCache = new Map<string, MessagesCacheEntry>();
// Дедуп параллельных запросов «головы» ленты одного топика (prefetch + own fetch).
const listMessagesHeadInflight = new Map<string, Promise<any>>();

/**
 * Синхронное чтение нормализованного кэша ленты топика — для мгновенной
 * инициализации state без ожидания сети. Отдаёт снимок даже «остывшим» (в
 * пределах STALE_MAX) — вызывающая сторона ревалидирует в фоне. Чистит и
 * возвращает null только если снимок старше жёсткого потолка.
 */
export function getCachedMessages(
  topicId: string,
): { items: any[]; hasMore: boolean; isStale: boolean } | null {
  const entry = messagesCache.get(topicId);
  if (!entry) return null;
  const age = Date.now() - entry.ts;
  if (age > MESSAGES_STALE_MAX_MS) {
    messagesCache.delete(topicId);
    return null;
  }
  return { items: entry.items, hasMore: entry.hasMore, isStale: age > MESSAGES_FRESH_MS };
}

/**
 * Запись снапшота ленты топика. Просто хранит то, что дали — фильтрацию
 * оптимистичных/неподтверждённых сообщений делает вызывающая сторона
 * (MessageThread), чтобы в кэш не попадали `_local_…` / `_status:'sending'`.
 */
export function setCachedMessages(
  topicId: string,
  items: any[],
  hasMore: boolean,
) {
  messagesCache.set(topicId, { items, hasMore, ts: Date.now() });
}

/**
 * Сброс кэша сообщений. Без аргумента — очистить весь; иначе — одну запись
 * (например при выходе из топика, который точно изменился извне).
 */
export function invalidateMessagesCache(topicId?: string) {
  if (!topicId) {
    messagesCache.clear();
    clearByPrefix(sessionStore(), RAW_MESSAGES_SS_PREFIX);
    return;
  }
  messagesCache.delete(topicId);
  persistRawMessagesDrop(topicId);
}

// ── Raw message-head persistence (тёплый старт ленты после F5) ─────────────────
function ssKeyRawMessages(topicId: string): string {
  return RAW_MESSAGES_SS_PREFIX + topicId;
}
function readRawIndex(): string[] {
  return readJSON<string[]>(sessionStore(), RAW_MESSAGES_INDEX_KEY) ?? [];
}
function writeRawIndex(ids: string[]): void {
  writeJSON(sessionStore(), RAW_MESSAGES_INDEX_KEY, ids);
}

/** Сохранить «сырой» снимок головы ленты (JSON-safe API-rows) с LRU-капом. */
function persistRawMessages(topicId: string, items: any[], hasMore: boolean): void {
  const ss = sessionStore();
  if (!ss) return;
  writeJSON(ss, ssKeyRawMessages(topicId), { items, hasMore, ts: Date.now() });
  // LRU: topicId в конец, старые сверх капа выкидываем.
  const index = readRawIndex().filter((id) => id !== topicId);
  index.push(topicId);
  while (index.length > RAW_MESSAGES_MAX_TOPICS) {
    const evicted = index.shift();
    if (evicted) {
      try {
        ss.removeItem(ssKeyRawMessages(evicted));
      } catch {
        /* ignore */
      }
    }
  }
  writeRawIndex(index);
}

function persistRawMessagesDrop(topicId: string): void {
  const ss = sessionStore();
  if (!ss) return;
  try {
    ss.removeItem(ssKeyRawMessages(topicId));
  } catch {
    /* ignore */
  }
  writeRawIndex(readRawIndex().filter((id) => id !== topicId));
}

/**
 * Тёплый старт ленты: «сырой» снимок головы (API-rows) из sessionStorage.
 * MessageThread нормализует его сам (messageFromApiRow) и рисует БЕЗ спиннера,
 * пока фоновый listMessages догоняет сеть. Возвращает null если снимка нет или
 * он старше STALE_MAX.
 */
export function getPersistedRawMessages(
  topicId: string,
): { items: any[]; hasMore: boolean } | null {
  const entry = readJSON<{ items: any[]; hasMore: boolean; ts: number }>(
    sessionStore(),
    ssKeyRawMessages(topicId),
  );
  if (!entry || !Array.isArray(entry.items)) return null;
  if (Date.now() - entry.ts > MESSAGES_STALE_MAX_MS) {
    persistRawMessagesDrop(topicId);
    return null;
  }
  return { items: entry.items, hasMore: entry.hasMore };
}

// ── Project → space resolution cache (тёплая гидрация сайдбара) ────────────────
// routeProjectRef → { projectId, spaceId } практически неизменно. Кэшируем в
// localStorage, чтобы ChatContext гидрировался синхронно и не блокировал весь
// сайдбар спиннером на время getProjectById (он ревалидирует в фоне).
const PROJECT_RESOLUTION_LS_PREFIX = 'chat_cache_projectres_';
export type ProjectResolution = { projectId: string; spaceId: string };

export function getCachedProjectResolution(ref: string): ProjectResolution | null {
  if (!ref) return null;
  const r = readJSON<ProjectResolution>(localStore(), PROJECT_RESOLUTION_LS_PREFIX + ref);
  if (r && typeof r.projectId === 'string' && typeof r.spaceId === 'string') return r;
  return null;
}

export function setCachedProjectResolution(ref: string, value: ProjectResolution): void {
  if (!ref || !value?.projectId || !value?.spaceId) return;
  writeJSON(localStore(), PROJECT_RESOLUTION_LS_PREFIX + ref, value);
}

async function chatFetch<T = any>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  // ВАЖНО: `...options` ДО `headers`. Иначе options.headers (например, только
  // { Idempotency-Key }) перезатирает весь computed-объект и теряется
  // Content-Type:application/json — chat-service NestJS body-parser не парсит
  // JSON, req.body становится {} → SendMessageExecutor получает body=undefined
  // → SQL null in NOT NULL column. Прод-инцидент 2026-06-04.
  const res = await fetch(`${CHAT_API_PREFIX}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...getBffRequestHeaders(),
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    // 401 = сессия мертва → сигнал auth-watchdog (AuthTabSync уведёт на /auth).
    if (res.status === 401) notifyBffUnauthorized();
    // pre-beta-gate 2.7: было `Chat API ${status}: ${тело}` — тело часто JSON,
    // пользователь мог увидеть его в тосте. throwApiError сохраняет status/
    // payload для errorToUserMessage у вызывающего кода.
    await throwApiError(res);
  }
  return res.json();
}

/**
 * Заголовок Idempotency-Key для write-эндпоинтов chat-service, на которые
 * на бэке навешан `IdempotencyInterceptor` (POST /messages, /channels,
 * /topics, /polls, /channels/:id/members). UUID v4 — на каждую логическую
 * операцию новый; для ретрая клиент передаёт тот же ключ через `idempotencyKey`.
 */
function idempotencyHeader(idempotencyKey?: string): Record<string, string> {
  return { 'Idempotency-Key': idempotencyKey ?? crypto.randomUUID() };
}

// ── Generic in-flight dedup для идемпотентных GET ──────────────────────────────
// Безопасная оптимизация СНИЖЕНИЯ нагрузки: схлопывает ОДНОВРЕМЕННЫЕ одинаковые
// GET-запросы в один общий промис. Ноль риска устаревания — это НЕ кэш, только
// коалесинг параллельных вызовов: как только запрос резолвится, ключ удаляется,
// и следующий вызов снова идёт в сеть. Закрывает «штормы» дублей (несколько
// PollMessageBlock с одним pollId, hover по реакциям, prefetch+open getChannel,
// параллельные batch comment-counts с одним набором id, открытие файла в двух
// местах). chat-service не имеет rate-limit/Redis-кэша на GET — поэтому дедуп на
// клиенте критичен, чтобы не множить тяжёлые SQL/S3-вызовы.
const inflightGet = new Map<string, Promise<any>>();
function dedupeGet<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflightGet.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const p = run().finally(() => {
    inflightGet.delete(key);
  });
  inflightGet.set(key, p);
  return p;
}

// Presigned read-URL вложений действителен часами; кэшируем на КЛИЕНТЕ короткое
// время, чтобы повторные открытия/превью одного файла не дёргали тяжёлую S3
// presign-генерацию (на бэке нет кэша, ~200-500мс на вызов). TTL заведомо меньше
// срока жизни presigned-ссылки.
const ATTACHMENT_URL_TTL_MS = 5 * 60_000;
const attachmentUrlCache = new Map<string, { url: string; ts: number }>();

/**
 * Экранирование ОДНОГО сегмента пути.
 *
 * Не косметика: часть id приезжает не из ответов API, а из Yjs-документа доски —
 * его правят все соавторы (и MCP-клиенты), поэтому `issueId` это по сути
 * пользовательский ввод. Без кодирования `issueId = "../.."` уводил запрос на
 * произвольный путь под `/api/bff` с кукой ТЕКУЩЕГО пользователя (confused deputy:
 * действие исполняется его правами, а подставил значение — автор карточки).
 * Соседний клиент задач (`utils/api/work.ts`) кодирует сегменты именно поэтому.
 */
const seg = (v: string): string => encodeURIComponent(String(v ?? ''));

export const chatApi = {
  // Channels
  listChannels: async (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';

    // Возвращаем из кэша если данные свежие (в пределах FRESH-окна).
    // readListChannelsEntry гидрирует in-memory из localStorage при промахе.
    const cached = listChannelsCache.get(qs) ?? readListChannelsEntry(qs);
    if (cached && Date.now() - cached.ts < LIST_CHANNELS_FRESH_MS) {
      return cached.data;
    }

    // Дедупликация параллельных запросов: не делаем два одинаковых HTTP-запроса
    const existing = listChannelsInflight.get(qs);
    if (existing) return existing;

    const promise = chatFetch<{ items?: Record<string, unknown>[] }>(`/channels${qs}`)
      .then((res) => {
        storeListChannelsEntry(qs, res);
        const items = res?.items;
        if (Array.isArray(items)) {
          items.forEach(cacheChannelFromList);
          preloadChannelImages(items);
        }
        return res;
      })
      .finally(() => listChannelsInflight.delete(qs));

    listChannelsInflight.set(qs, promise);
    return promise;
  },
  getChannel: async (id: string) => {
    const cached = getCachedChannel(id);
    if (cached) return cached;
    // Дедуп: prefetch по hover и реальное открытие канала легко стартуют два
    // одинаковых GET /channels/:id до того, как первый наполнит кэш.
    const res = await dedupeGet(`channel:${id}`, () => chatFetch(`/channels/${id}`));
    // Обновляем кэш полными данными (с members) + персист в localStorage.
    if (res && typeof res === 'object') {
      storeChannelBundle(id, res);
    }
    return res;
  },
  createChannel: (body: any, idempotencyKey?: string) =>
    chatFetch('/channels', {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),

  /**
   * Резолв канала по человекочитаемому @handle в пределах пространства.
   * Используется лендинг-роутом `/c/[handle]` для разворачивания короткой ссылки
   * в реальный channelId. 404 → null (handle не найден в этом пространстве).
   */
  resolveChannelByHandle: (
    handle: string,
    spaceId: string,
  ): Promise<{
    id: string;
    project_id: string | null;
    space_id: string;
    type: string;
    name: string | null;
    handle: string;
  } | null> => {
    const qs = new URLSearchParams({ handle, space_id: spaceId }).toString();
    return chatFetch(`/channels/resolve?${qs}`).catch((err) => {
      // 404 — handle нет в этом пространстве: для перебора пространств это норма.
      if (err instanceof Error && /\b404\b/.test(err.message)) return null;
      throw err;
    });
  },

  /**
   * Погасить инвайт-токен короткой ссылки: авто-добавляет текущего пользователя
   * в участники канала. 410 — токен истёк (не критично для открытия канала).
   */
  redeemInvite: (token: string): Promise<{ channel_id: string }> =>
    chatFetch(`/channels/invite/${encodeURIComponent(token)}/redeem`, {
      method: 'POST',
      headers: idempotencyHeader(),
    }),
  updateChannel: (id: string, body: any) =>
    chatFetch(`/channels/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteChannel: (id: string) =>
    chatFetch(`/channels/${id}`, { method: 'DELETE' }),
  addMember: (channelId: string, body: any, idempotencyKey?: string) =>
    chatFetch(`/channels/${channelId}/members`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),
  removeMember: (channelId: string, employeeId: string) =>
    chatFetch(`/channels/${channelId}/members/${encodeURIComponent(employeeId)}`, {
      method: 'DELETE',
    }),
  /** owner/admin: повысить участника до админа / снять роль админа (обратно в member). */
  updateMemberRole: (channelId: string, employeeId: string, role: 'admin' | 'member') =>
    chatFetch(`/channels/${channelId}/members/${encodeURIComponent(employeeId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  // ── Access & invites (закрытые каналы) ────────────────────────────────────
  // Инвайт-ссылка: owner/admin генерит одноразовый токен (TTL ~1 день).
  // Запрос доступа: не-участник стучится владельцу/админам, те подтверждают.
  /** owner/admin: выдать токен инвайт-ссылки (срок жизни задаёт бэк, обычно 24ч). */
  createInviteToken: (channelId: string) =>
    chatFetch<{ token: string; expires_at: string }>(
      `/channels/${channelId}/invite-token`,
      { method: 'POST', headers: idempotencyHeader() },
    ),
  /** Запросить доступ к закрытому каналу. already_member — уже состоит, можно открыть. */
  requestChannelAccess: (channelId: string) =>
    chatFetch<{ id: string; status: 'pending' } | { status: 'already_member' }>(
      `/channels/${channelId}/access-requests`,
      { method: 'POST', headers: idempotencyHeader() },
    ),
  /** owner/admin: список pending-запросов на доступ к каналу. */
  listAccessRequests: (channelId: string) =>
    chatFetch<{
      items: {
        id: string;
        requester_employee_id: string;
        requester_name: string;
        requested_at: string;
      }[];
    }>(`/channels/${channelId}/access-requests`),
  /** owner/admin: подтвердить запрос → запрашивающий становится участником. */
  approveAccessRequest: (channelId: string, reqId: string) =>
    chatFetch<{ ok: true }>(
      `/channels/${channelId}/access-requests/${reqId}/approve`,
      { method: 'POST', headers: idempotencyHeader() },
    ),

  // Pin/unpin channel in current user's list
  pinChannel: (channelId: string) =>
    chatFetch<{ pinned_at: string | null }>(`/channels/${channelId}/pin`, {
      method: 'POST',
    }),
  unpinChannel: (channelId: string) =>
    chatFetch(`/channels/${channelId}/pin`, { method: 'DELETE' }),

  // DM; при employee_id = текущий пользователь — канал «Заметки» (notes), не ЛС с собой
  findOrCreateDM: (body: { employee_id: string; space_id: string }) =>
    chatFetch('/dm', { method: 'POST', body: JSON.stringify(body) }),

  // Topics
  listTopics: (channelId: string, params?: Record<string, string>) => {
    // Кэшируем/персистим только дефолтный вызов (без доп. параметров) —
    // именно его читает ChannelView для seed без спиннера.
    if (params && Object.keys(params).length > 0) {
      const qs = '?' + new URLSearchParams(params).toString();
      return chatFetch(`/channels/${channelId}/topics${qs}`);
    }
    const cached = readTopicsEntry(channelId);
    if (cached && Date.now() - cached.ts < TOPICS_FRESH_MS) {
      return Promise.resolve(cached.data);
    }
    const existing = topicsInflight.get(channelId);
    if (existing) return existing;
    const promise = chatFetch(`/channels/${channelId}/topics`)
      .then((res) => {
        storeTopicsEntry(channelId, res);
        return res;
      })
      .finally(() => topicsInflight.delete(channelId));
    topicsInflight.set(channelId, promise);
    return promise;
  },
  createTopic: (channelId: string, body: any, idempotencyKey?: string) =>
    chatFetch(`/channels/${channelId}/topics`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),
  updateTopic: (
    topicId: string,
    body: { title?: string; pinned?: boolean; closed_at?: string | null; icon?: string | null },
  ) =>
    chatFetch(`/topics/${topicId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTopic: (topicId: string) =>
    chatFetch(`/topics/${topicId}`, { method: 'DELETE' }),

  /**
   * Bulk-reorder топиков: фронт шлёт массив id в новом порядке.
   * Бэк проставит sort_order = 10, 20, 30, ... в одной tx под advisory-lock
   * на канале. Используется DnD в ChannelView (см. блокер 9).
   */
  reorderTopics: (channelId: string, topicIds: string[]) =>
    chatFetch(`/channels/${channelId}/topics/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ topic_ids: topicIds }),
    }),

  // Messages
  listMessages: (topicId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    const isHead = !params || !('before_seq' in params); // голова, не пагинация
    if (!isHead) {
      return chatFetch<{ items?: Record<string, unknown>[]; has_more?: boolean }>(
        `/topics/${topicId}/messages${qs}`,
      );
    }
    // Дедуп головы: prefetch (этап ③/⑤) и собственный fetch MessageThread не
    // должны слать два одинаковых запроса. Inflight держим до резолва.
    const existing = listMessagesHeadInflight.get(topicId);
    if (existing) return existing;
    const promise = chatFetch<{ items?: Record<string, unknown>[]; has_more?: boolean }>(
      `/topics/${topicId}/messages${qs}`,
    )
      .then((res) => {
        // Персистим «сырой» снимок головы в sessionStorage для тёплого старта после F5.
        if (res && Array.isArray(res.items)) {
          persistRawMessages(topicId, res.items, res.has_more || false);
        }
        return res;
      })
      .finally(() => listMessagesHeadInflight.delete(topicId));
    listMessagesHeadInflight.set(topicId, promise);
    return promise;
  },
  sendMessage: (topicId: string, body: any, idempotencyKey?: string) =>
    chatFetch(`/topics/${topicId}/messages`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),
  editMessage: (messageId: string, body: any) =>
    chatFetch(`/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteMessage: (messageId: string) =>
    chatFetch(`/messages/${messageId}`, { method: 'DELETE' }),

  // Reactions
  addReaction: (messageId: string, emoji: string) =>
    chatFetch(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
      method: 'PUT',
    }),
  removeReaction: (messageId: string, emoji: string) =>
    chatFetch(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
      method: 'DELETE',
    }),

  // Polls
  createPoll: (topicId: string, body: any, idempotencyKey?: string) =>
    chatFetch(`/topics/${topicId}/polls`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),
  votePoll: (pollId: string, optionIds: string[]) =>
    chatFetch(`/polls/${pollId}/vote`, {
      method: 'PUT',
      body: JSON.stringify({ option_ids: optionIds }),
    }),
  /** Снять свой голос — для пункта меню «Переголосовать». */
  unvotePoll: (pollId: string) =>
    chatFetch(`/polls/${pollId}/vote`, { method: 'DELETE' }),
  // Дедуп: на ленте несколько блоков одного опроса или mount+live-update могут
  // дёрнуть GET /polls/:id одновременно. Кэш НЕ ставим — `my_votes` зависит от
  // пользователя и обновляется по WS `poll.updated`, поэтому коалесим только
  // одновременные вызовы (без устаревания).
  getPoll: (pollId: string) => dedupeGet(`poll:${pollId}`, () => chatFetch(`/polls/${pollId}`)),

  // Attachments
  createUploadIntent: (body: any) =>
    chatFetch('/attachments/upload-intent', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  completeUpload: (attachmentId: string) =>
    chatFetch(`/attachments/${attachmentId}/complete`, { method: 'POST' }),
  /** Presigned GET для просмотра/скачивания (bucket приватный). */
  getAttachmentReadUrl: (attachmentId: string): Promise<{ url: string }> => {
    const cached = attachmentUrlCache.get(attachmentId);
    if (cached && Date.now() - cached.ts < ATTACHMENT_URL_TTL_MS) {
      return Promise.resolve({ url: cached.url });
    }
    return dedupeGet(`att:${attachmentId}`, () =>
      chatFetch<{ url: string }>(`/attachments/${attachmentId}/read-url`).then((r) => {
        if (r?.url) attachmentUrlCache.set(attachmentId, { url: r.url, ts: Date.now() });
        return r;
      }),
    );
  },

  // Search
  searchMessages: (q: string, limit?: number) =>
    chatFetch(`/search/messages?q=${encodeURIComponent(q)}&limit=${limit || 50}`),
  searchPeople: (q: string, spaceId: string, projectId?: string, limit?: number) => {
    const params: Record<string, string> = { q, space_id: spaceId, limit: String(limit || 20) };
    if (projectId) params.project_id = projectId;
    return chatFetch(`/people/search?${new URLSearchParams(params)}`);
  },
  // Поиск каналов по названию — секция «Каналы» сверху глобального поиска.
  searchChannels: (q: string, spaceId?: string, limit?: number) => {
    const params: Record<string, string> = { q, limit: String(limit || 20) };
    if (spaceId) params.space_id = spaceId;
    return chatFetch(`/search/channels?${new URLSearchParams(params)}`);
  },

  // Sync ticket
  getSyncTicket: () =>
    chatFetch('/sync-ticket', { method: 'POST' }),

  // Notifications (новый unified API)
  notifications: {
    list: (params?: { limit?: number; before?: string; only_unread?: boolean; type_prefix?: string }) => {
      const qs = params
        ? '?' + new URLSearchParams(
            Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
              if (v != null) acc[k] = String(v);
              return acc;
            }, {}),
          ).toString()
        : '';
      return chatFetch(`/notifications${qs}`);
    },
    unreadCount: () => chatFetch('/notifications/unread-count'),
    markRead: (ids: string[]) =>
      chatFetch('/notifications/mark-read', { method: 'POST', body: JSON.stringify({ ids }) }),
    markAllRead: () =>
      chatFetch('/notifications/mark-read', { method: 'POST', body: JSON.stringify({ all: true }) }),
    getPreferences: () => chatFetch('/notifications/preferences'),
    updatePreferences: (patch: Record<string, unknown>) =>
      chatFetch('/notifications/preferences', { method: 'PUT', body: JSON.stringify(patch) }),
    muteChannel: (channelId: string, muted: boolean) =>
      chatFetch(`/notifications/mute/${channelId}`, {
        method: 'PATCH',
        body: JSON.stringify({ muted }),
      }),
    /** Снапшот последнего статуса MCP-агента — catch-up после обрыва WS. */
    agentActivityLast: () => chatFetch('/notifications/agent-activity/last'),
  },

  // Reactions: список юзеров (для popover «кто поставил»)
  // Дедуп: popover при открытии триггерит вызов дважды (open + смена count),
  // а одинаковый emoji на разных hover'ах — серия. Коалесим одновременные.
  listReactionUsers: (messageId: string, emoji: string) =>
    dedupeGet(`react:${messageId}:${emoji}`, () =>
      chatFetch(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/users`),
    ),

  // Поиск внутри топика (Ctrl+F)
  searchInTopic: (topicId: string, q: string, limit?: number) =>
    chatFetch(
      `/topics/${topicId}/search?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ''}`,
    ),

  // Бейдж непрочитанных комментариев на карточках задач.
  // Дедуп: несколько представлений доски (board + list view) могут запросить один
  // и тот же набор issue_ids одновременно — коалесим в один batch-запрос.
  getIssueCommentCounts: (issueIds: string[]) => {
    const ids = issueIds.join(',');
    return dedupeGet(
      `issuecc:${ids}`,
      () =>
        chatFetch<{
          items: { issue_id: string; has_comments: boolean; unread_count: number; last_comment_at: string | null }[];
        }>(`/issues/comment-counts?issue_ids=${encodeURIComponent(ids)}`),
    );
  },

  // Issue comments
  getIssueComments: (issueId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return chatFetch(`/issues/${seg(issueId)}/comments${qs}`);
  },
  addIssueComment: (issueId: string, body: any) =>
    chatFetch(`/issues/${seg(issueId)}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getIssueUnreadCount: (issueId: string) =>
    chatFetch(`/issues/${seg(issueId)}/comments/unread`),

  // Document comments (зеркало issue comments — канал-на-документ в группе «Комментарии»)
  getDocumentComments: (documentId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return chatFetch(`/documents/${seg(documentId)}/comments${qs}`);
  },
  addDocumentComment: (documentId: string, body: Record<string, unknown>) =>
    chatFetch(`/documents/${seg(documentId)}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Идемпотентно создать канал+топик документа БЗ, НЕ отправляя сообщение.
   * Нужен, чтобы первый комментарий можно было послать обычным
   * `sendMessage(topicId, …)`: только generic-эндпоинт публикует уведомления
   * (в т.ч. по @-упоминаниям), а `addDocumentComment` шлёт мимо них.
   */
  ensureDocumentChannel: (
    documentId: string,
    body: { document_title: string; space_id: string; project_id: string },
  ) =>
    chatFetch(`/documents/${seg(documentId)}/channel`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getDocumentUnreadCount: (documentId: string) =>
    chatFetch(`/documents/${seg(documentId)}/comments/unread`),

  // ── Message comments (комментарии к сообщениям чата) ──────────────────────
  // Комментарий = обычное message в скрытом канале комментариев host-канала,
  // с отдельным топиком на каждое исходное сообщение (см. message-comments
  // backend-модуль). Лента/вид — тот же MessageThread, что и обычные сообщения.

  /** Лента комментариев к сообщению. thread=null, если ещё не было ни одного. */
  getMessageComments: (
    messageId: string,
    params?: Record<string, string>,
  ) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return chatFetch<{
      thread: { comment_channel_id: string; comment_topic_id: string } | null;
      messages: Record<string, unknown>[];
      has_more: boolean;
    }>(`/messages/${messageId}/comments${qs}`);
  },
  /**
   * Гарантирует тред комментариев (ленивое создание при заходе на страницу) и
   * возвращает его id + обогащённое исходное сообщение для шапки. Нужен, потому
   * что страница комментариев монтирует обычный MessageThread по comment_topic_id.
   */
  /**
   * Резолв треда комментариев по comment_topic_id (топику скрытого канала
   * комментариев) → исходное сообщение + host-канал/топик + превью. Для deeplink
   * из уведомления о комментарии: открыть экран комментариев «как пользователь».
   * null — это не comment-топик (обычный канал/топик).
   */
  resolveCommentThread: (commentTopicId: string) =>
    chatFetch<{
      source_message_id: string;
      host_channel_id: string;
      host_topic_id: string;
      source_preview: string;
    } | null>(
      `/messages/comments/resolve-thread?comment_topic_id=${encodeURIComponent(commentTopicId)}`,
    ).catch(() => null),
  ensureMessageCommentThread: (
    messageId: string,
    hostChannelId: string,
    idempotencyKey?: string,
  ) =>
    chatFetch<{
      thread: { comment_channel_id: string; comment_topic_id: string };
      source: Record<string, unknown>;
    }>(`/messages/${messageId}/comments/ensure`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify({ host_channel_id: hostChannelId }),
    }),
  /** Добавить комментарий. host_channel_id нужен бэку для guard (comments_enabled + членство). */
  addMessageComment: (
    messageId: string,
    body: {
      host_channel_id: string;
      body: unknown;
      reply_to_message_id?: string;
      attachment_ids?: string[];
    },
    idempotencyKey?: string,
  ) =>
    chatFetch(`/messages/${messageId}/comments`, {
      method: 'POST',
      headers: idempotencyHeader(idempotencyKey),
      body: JSON.stringify(body),
    }),
  /** Bulk-счётчики «N комментариев» для бейджей под видимыми сообщениями. */
  getMessageCommentCounts: (messageIds: string[]) =>
    chatFetch<{
      items: {
        source_message_id: string;
        comment_count: number;
        has_comments: boolean;
        last_comment_at: string | null;
      }[];
    }>(`/messages/comment-counts`, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    }),

  /**
   * Пакетно тянет активные «флаги» задач (последний флаг на задачу).
   * Используется на доске, чтобы подсветить карточки задач с открытым флагом.
   */
  getIssueFlags: (issueIds: string[]) => {
    if (!issueIds.length) {
      return Promise.resolve(
        [] as Array<{
          issue_id: string;
          message_id: string;
          body: { text?: string; format?: string; is_flag?: boolean };
          created_at: string;
          author_employee_id: string;
        }>,
      );
    }
    const qs = '?issue_ids=' + encodeURIComponent(issueIds.join(','));
    return chatFetch<
      Array<{
        issue_id: string;
        message_id: string;
        body: { text?: string; format?: string; is_flag?: boolean };
        created_at: string;
        author_employee_id: string;
      }>
    >(`/issues/flags${qs}`);
  },

  // Audio channels
  createAudioChannel: (topicId: string) =>
    chatFetch(`/topics/${topicId}/audio-channels`, {
      method: 'POST',
      headers: idempotencyHeader(),
    }),

  joinAudioChannel: (channelId: string) =>
    chatFetch(`/audio-channels/${channelId}/join`, { method: 'POST' }),

  leaveAudioChannel: (channelId: string) =>
    chatFetch(`/audio-channels/${channelId}/leave`, { method: 'POST' }),

  closeAudioChannel: (channelId: string) =>
    chatFetch(`/audio-channels/${channelId}`, { method: 'DELETE' }),

  /** Редактирование закрытого аудио-канала создателем: итоги и/или название. */
  updateAudioChannelMeta: (
    channelId: string,
    patch: { summary?: string; name?: string },
  ) =>
    chatFetch(`/audio-channels/${channelId}/summary`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  getAudioChannelState: (channelId: string) =>
    chatFetch(`/audio-channels/${channelId}`),

  /**
   * Эфемерные TURN-креды (TURN REST API). Read-only: секрет в бандл не попадает,
   * креды одноразовые с TTL. Фронт кэширует их до истечения (см. useAudioChannel).
   */
  getTurnCredentials: (): Promise<{
    username: string;
    credential: string;
    ttl: number;
    urls: string[];
  }> => chatFetch('/turn-credentials'),
};

// ── Prefetch helpers (предзагрузка по намерению / при восстановлении маршрута) ──
// Лёгкие эвристики: греют кэши заранее, чтобы клик/навигация попадали в готовые
// данные. Все ошибки глушатся (фоновая работа), дедуп — за счёт inflight-карт
// внутри самих chatApi-методов и кэшей.

/** Греет bundle канала + список его топиков (hover по строке канала, restore). */
export function prefetchChannelBundle(channelId: string): void {
  if (!channelId) return;
  void chatApi.getChannel(channelId).catch(() => {});
  void chatApi.listTopics(channelId).catch(() => {});
}

/**
 * Греет «голову» ленты топика (hover по строке топика, restore последнего
 * открытого треда). Наполняет сырой sessionStorage-снимок для тёплого старта.
 */
export function prefetchTopicMessages(topicId: string): void {
  if (!topicId) return;
  // Уже есть свежий нормализованный снимок в памяти — сеть не нужна.
  const cached = getCachedMessages(topicId);
  if (cached && !cached.isStale) return;
  void chatApi.listMessages(topicId, { limit: '50' }).catch(() => {});
}
